/**
 * 크랭크–로커 풀이 — <b>맞물린 것은 함께 움직인다</b>(SR-SIM-09).
 *
 * <p>기어는 비율 하나로 끝나지만 링크는 그렇지 않다. 크랭크가 돌면 커넥팅 로드가 로커를 밀고,
 * 로커는 <b>돌다가 되돌아온다</b> — 각도끼리 비례하지 않는다. 그 관계는 '로드 길이가 변하지 않는다'
 * 는 조건에서만 나오고, 풀어 주지 않으면 조립품의 절반이 제자리에 멈춰 선다.
 *
 * <p>같은 풀이가 Campus 의 굽는 스크립트(`model-anim-glb.mjs`)에 이미 있다. 다만 거기서는
 * <b>시간</b> 격자 위에서 미리 풀어 키프레임으로 굽는다 — 애니메이션은 시간이 정하기 때문이다.
 * 시뮬레이터에는 시간이 아니라 <b>모터 각도</b>가 온다. 그래서 같은 기하를 시간 대신
 * <b>크랭크 각 φ 의 함수</b>로 다시 적는다. 식은 같고 변수만 바뀐다.
 *
 * <p>여기 있는 것은 <b>산수뿐이다</b>: three 도 장면도 모른다. 좌표는 정지 자세의 모델 좌표(cm)이고,
 * 축·피벗·핀은 모두 모형 자산이 실어 준 값이다 — 이 파일이 새로 정하는 것은 없다.
 */
import { atan2, cos, hypot, sin } from "./det-math";   // 결정론 수학 — 엔진마다 끝자리가 갈리지 않게(FIELD-CRQ-001 D-4)

export type Vec3 = readonly [number, number, number];

const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const mul = (a: Vec3, k: number): Vec3 => [a[0] * k, a[1] * k, a[2] * k];
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: Vec3, b: Vec3): Vec3 =>
  [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const len = (a: Vec3) => hypot(a[0], a[1], a[2]);
const unit = (a: Vec3): Vec3 => { const l = len(a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };

/** 축 u 둘레로 v 를 θ 만큼 돌린다(로드리게스) */
const rot = (u: Vec3, th: number, v: Vec3): Vec3 => {
  const c = cos(th), s = sin(th), d = dot(u, v), cx = cross(u, v);
  return [v[0] * c + cx[0] * s + u[0] * d * (1 - c),
          v[1] * c + cx[1] * s + u[1] * d * (1 - c),
          v[2] * c + cx[2] * s + u[2] * d * (1 - c)];
};

/** 두 방향 사이의 부호 있는 각(축 u 기준) */
const signedAngle = (u: Vec3, from: Vec3, to: Vec3) =>
  atan2(dot(u, cross(from, to)), dot(from, to));

/**
 * 크랭크가 한 걸음에 도는 최대 각(라디안). 이보다 크게 뛰면 나눠서 푼다.
 *
 * <p>링크에는 <b>해가 둘</b> 있다(위로 꺾인 팔꿈치와 아래로 꺾인 팔꿈치). 이전 해 근처에서 풀어
 * 늘 같은 쪽을 고르는데, 한 번에 많이 뛰면 반대쪽으로 넘어가 <b>팔이 뒤집힌다</b>.
 * 모터 각은 보통 조금씩 오지만 되돌리기·빨리감기에서는 크게 뛴다 — 그때만 잘게 나눈다.
 */
const STEP = Math.PI / 18;                                   // 10°

/** 로커 한 벌 — 정지 자세에서 한 번 만들고 계속 쓴다 */
export type Rocker = {
  /**
   * 크랭크 각 φ(rad)에서 로커 각 θ(rad). <b>못 풀면 null</b> — 링크가 닿지 않는 곳이다.
   * @param prev 직전 해(rad). 해가 둘이라 이전 것 근처를 고른다
   * @param prevPhi 직전 크랭크 각(rad). 많이 뛰면 나눠서 따라간다
   */
  theta: (phi: number, prev: number, prevPhi: number) => number | null;
  /** 커넥팅 로드 자신의 각(rad) — 로드는 크랭크의 자식이라 부모 회전 φ 를 뺀다 */
  conrod: (phi: number, theta: number) => number;
};

export type RockerSpec = {
  /** 로커의 축·피벗 */
  axis: Vec3; pivot: Vec3;
  /** 크랭크의 축·피벗 */
  crankAxis: Vec3; crankPivot: Vec3;
  /** 정지 자세의 크랭크 핀 · 로커 끝(로드의 두 끝) */
  pin: Vec3; attach: Vec3;
  /**
   * 크랭크가 <b>미끄러지는 관절</b>인가(불도저: 랙이 블레이드를 민다).
   * <p>그러면 핀이 도는 대신 축 방향으로 <b>평행 이동</b>한다 — 크랭크 각 자리에 이동 거리(cm)가 온다.
   * 로드는 그 크랭크의 자식이어도 <b>돌지 않으므로</b>, 로드 각에서 빼 줄 부모 회전이 없다.
   */
  crankSlides?: boolean;
};

/**
 * 크랭크–로커 4절 링크.
 *
 * <p>크랭크 피벗에서 `pin` 까지가 크랭크, 로커 피벗에서 `attach` 까지가 로커 팔,
 * 둘 사이가 <b>커넥팅 로드</b>다. 로드 길이는 변하지 않는다 — 그 한 가지가 모든 것을 정한다.
 * 크랭크 각 φ 로 핀 위치 P 가 정해지면, |로커끝(θ) − P| = 로드 길이를 만족하는 θ 를 찾는다.
 * 닫힌 식이 없어 <b>뉴턴법</b>으로 좁혀 간다(굽는 스크립트와 같은 방법).
 */
export function makeRocker(s: RockerSpec): Rocker {
  const u = unit(s.axis), uc = unit(s.crankAxis);
  const O = s.pivot, C = s.crankPivot;
  const p = sub(s.pin, C);                                   // 크랭크 피벗 → 핀
  const a = sub(s.attach, O);                                // 로커 피벗 → 로커 끝
  const L = len(sub(s.attach, s.pin));                       // 커넥팅 로드 길이
  const d0 = sub(s.attach, s.pin);                           // 정지 자세의 로드 방향

  const slides = !!s.crankSlides;
  // 미끄러지는 크랭크는 핀을 축 방향으로 밀기만 한다(돌지 않는다)
  const pinAt = (phi: number) => (slides ? add(s.pin, mul(uc, phi)) : add(C, rot(uc, phi, p)));
  const endAt = (th: number) => add(O, rot(u, th, a));

  /** 한 걸음 — 이전 해에서 뉴턴법으로 좁힌다. 못 좁히면 null */
  const step = (phi: number, seed: number): number | null => {
    const P = pinAt(phi);
    const g = (x: number) => len(sub(endAt(x), P)) - L;
    let th = seed;
    for (let k = 0; k < 30; k++) {
      const g0 = g(th);
      if (Math.abs(g0) < 1e-7) return th;
      const dg = (g(th + 1e-5) - g0) / 1e-5;
      if (!dg) break;
      th -= g0 / dg;
      if (!Number.isFinite(th)) return null;
    }
    return Math.abs(g(th)) < 1e-3 ? th : null;               // 로드가 닿지 않는다 — 부르는 쪽이 제자리에 둔다
  };

  return {
    theta(phi, prev, prevPhi) {
      const span = phi - prevPhi;
      // 미끄러지는 크랭크는 걸음이 각이 아니라 거리(cm)다 — 0.5cm 면 레고 한 칸의 반이다
      const step0 = slides ? 0.5 : STEP;
      const n = Math.min(Math.max(Math.ceil(Math.abs(span) / step0), 1), 4096);   // 아무리 뛰어도 셈은 유한하게
      let th = prev;
      for (let i = 1; i <= n; i++) {
        const got = step(prevPhi + (span * i) / n, th);
        if (got === null) return i === 1 ? null : th;         // 닿지 않는 데까지 왔다 — 거기서 멈춘다
        th = got;
      }
      return th;
    },
    conrod(phi, theta) {
      // 로드의 월드 기울기에서 부모(크랭크) 회전을 뺀다 — 로드는 크랭크에 매달려 있다.
      // 미끄러지는 크랭크는 돌지 않으므로 뺄 것이 없다.
      return signedAngle(u, d0, sub(endAt(theta), pinAt(phi))) - (slides ? 0 : phi);
    },
  };
}

/** 슬라이더 한 벌 — 크랭크 각 φ(rad)와 직전 미끄럼량에서 로드 각과 새 미끄럼량 */
export type Slider = (phi: number, prevSlide: number) => { angle: number; slide: number } | null;

export type SliderSpec = {
  /** 로드(이 관절)의 축 */
  axis: Vec3;
  /** 크랭크의 축·피벗 */
  crankAxis: Vec3; crankPivot: Vec3;
  /** 정지 자세의 크랭크 핀 · 로드 반대쪽 끝 */
  pin: Vec3; attach: Vec3;
  /** 반대쪽 끝이 미끄러지는 직선의 방향 */
  guide: Vec3;
};

/**
 * 슬라이더–크랭크 — 회전을 직선 왕복으로 바꾸는 링크(원숭이 팔·펌프).
 *
 * <p>로드 위끝은 크랭크 핀에 걸려 있고 아래끝은 <b>가이드 직선 위를 미끄러진다</b>.
 * 로드 길이가 변하지 않으므로 아래끝의 위치는 2차식 하나로 정해지는데, 해가 둘이라
 * (직선이 원을 두 번 지난다) 이전 것과 가까운 쪽을 고른다.
 *
 * @returns 크랭크 각 φ(rad) → 로드 각(rad). 가이드에 닿지 않으면 null
 */
export function makeSlider(s: SliderSpec): Slider {
  const u = unit(s.axis), uc = unit(s.crankAxis), g = unit(s.guide);
  const C = s.crankPivot, p = sub(s.pin, C), A0 = s.attach;
  const d0 = sub(A0, s.pin), L = len(d0);

  return (phi, prevS) => {
    const P = add(C, rot(uc, phi, p));
    const w = sub(A0, P), b = dot(w, g), c = dot(w, w) - L * L, disc = b * b - c;
    if (disc < 0) return null;                               // 로드가 가이드에 닿지 않는다
    const r = Math.sqrt(disc), s1 = -b + r, s2 = -b - r;
    const slide = Math.abs(s1 - prevS) <= Math.abs(s2 - prevS) ? s1 : s2;
    const d = sub(add(A0, mul(g, slide)), P);
    return { angle: signedAngle(u, d0, d) - phi, slide };
  };
}

/** 2축 공간 링크 한 벌 — 선회각 β 와 피치각 α 를 <b>함께</b> 낸다 */
export type Link = (theta: number, phi: number) => { swivel: number; pitch: number } | null;

export type LinkSpec = {
  /** 크랭크(회전판)의 축·피벗 */
  crankAxis: Vec3; crankPivot: Vec3;
  /** 로커(발)의 축·피벗 */
  rockerAxis: Vec3; rockerPivot: Vec3;
  /** 정지 자세의 세 점: 크랭크 핀 · 윗핀 · 로커 쪽 끝 */
  pin: Vec3; top: Vec3; attach: Vec3;
  /** 윗핀이 접히는 축(피치) */
  pinAxis: Vec3;
};

/**
 * <b>2축 공간 링크</b> — 워킹맨 발목.
 *
 * <p>여기까지의 링크는 전부 <b>한 평면</b> 안에서 돌았다. 그런데 회전판 위의 핀과 발 위의 핀은
 * 같은 평면에 있지 않다: 막대가 <b>비틀리면서</b> 따라간다. 그래서 각이 하나로는 부족하고 둘이다 —
 * 회전판 축으로 <b>선회</b>하는 각 β 와, 윗핀에서 <b>접히는</b> 각 α.
 *
 * <p>스펙은 이 둘을 <b>두 관절</b>에 나눠 적는다(`part: "swivel"` · `"pitch"`). 값은 따로 나오지
 * 않는다 — 같은 막대의 두 마디라 <b>함께</b> 풀어야 한다. 그래서 풀이 한 벌을 둘이 나눠 쓴다.
 *
 * <p>닫힌 식이 없고 미지수가 둘이라 <b>가우스–뉴턴</b>으로 좁힌다(굽는 스크립트와 같은 방법).
 * 맞추는 것은 <b>방향</b>뿐이다: 막대 끝이 발의 핀을 가리키기만 하면 된다. 길이는 맞추지 않는다 —
 * 실물에서 그 끝은 구멍에 끼워져 미끄러진다.
 *
 * @returns 크랭크 각 θ·로커 각 φ(rad)에서 선회·피치 각(rad). 못 좁히면 null
 */
export function makeLink(s: LinkSpec): Link {
  const uc = unit(s.crankAxis), ur = unit(s.rockerAxis), ub = unit(s.pinAxis);
  const C = s.crankPivot, D = s.rockerPivot;
  const p = sub(s.pin, C), q = sub(s.attach, D);
  const t0 = sub(s.top, s.pin), l0 = sub(s.attach, s.top);

  // 아랫마디에 길이가 없으면 가리킬 방향 자체가 없다. 그런데 residual 은 0 − 0 = 0 이라
  // **풀었다고 답한다** — 두 각이 아무 값이어도 통과하니 화면에는 아무 뜻 없는 수가 실린다.
  const aimable = len(l0) > 1e-9;
  let a = 0, b = 0;                                          // 지난 해 — 미지수가 둘이라 씨앗이 더 중요하다
  let seen: { th: number; ph: number; swivel: number; pitch: number } | null = null;

  /** 지금 자세에서 방향이 얼마나 어긋나는가 — 0 이 되게 (α, β) 를 찾는다 */
  const residual = (P: Vec3, Q: Vec3, th: number, al: number, be: number): Vec3 => {
    const T = add(P, rot(uc, th + be, t0));                  // 윗핀은 회전판과 함께 돌고, 선회만큼 더 돈다
    const dir = unit(rot(uc, th + be, rot(ub, al, l0)));     // 막대 아랫마디가 가리키는 쪽
    const want = unit(sub(Q, T));                            // 실제로 가리켜야 하는 쪽
    return sub(dir, want);
  };

  const step = (th: number, ph: number) => {
    const P = add(C, rot(uc, th, p)), Q = add(D, rot(ur, ph, q));
    const F = (al: number, be: number) => residual(P, Q, th, al, be);
    for (let k = 0; k < 25; k++) {
      const f0 = F(a, b);
      if (len(f0) < 1e-7) break;
      const h = 1e-5;
      const fa = F(a + h, b), fb = F(a, b + h);
      const Ja = mul(sub(fa, f0), 1 / h), Jb = mul(sub(fb, f0), 1 / h);   // 각을 조금 흔들어 본 기울기
      const aa = dot(Ja, Ja), ab = dot(Ja, Jb), bb = dot(Jb, Jb);
      const ra = dot(Ja, f0), rb = dot(Jb, f0);
      const det = aa * bb - ab * ab;
      if (Math.abs(det) < 1e-12) break;                      // 두 각이 같은 일을 한다 — 더 좁힐 수 없다
      a -= (bb * ra - ab * rb) / det;
      b -= (aa * rb - ab * ra) / det;
      if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
    }
    return len(F(a, b)) < 1e-3;                              // 방향이 이만큼도 안 맞으면 푼 것이 아니다
  };

  return (theta, phi) => {
    if (!aimable) return null;
    // 같은 자세를 두 번 묻는다 — `swivel` 과 `pitch` 가 같은 풀이를 나눠 쓰기 때문이다.
    // 두 번 풀면 답은 같아도 지난 해가 두 걸음 나아가므로, 한 번만 풀고 되돌려 준다.
    if (seen && seen.th === theta && seen.ph === phi) return { swivel: seen.swivel, pitch: seen.pitch };
    const from = seen ?? { th: 0, ph: 0, swivel: 0, pitch: 0 };
    const dth = theta - from.th, dph = phi - from.ph;
    const n = Math.min(Math.max(Math.ceil(Math.max(Math.abs(dth), Math.abs(dph)) / STEP), 1), 4096);
    const a0 = a, b0 = b;
    for (let i = 1; i <= n; i++) {
      if (!step(from.th + (dth * i) / n, from.ph + (dph * i) / n)) {
        a = a0; b = b0;                                      // 못 좁혔다 — 지난 자세를 흐트러뜨리지 않고 돌려보낸다
        return null;
      }
    }
    seen = { th: theta, ph: phi, swivel: b, pitch: a };
    return { swivel: b, pitch: a };
  };
}

/** 5절 링크 한 벌 — 두 크랭크 각(rad)에서 이 링크의 각(rad). 두 링크가 닿지 않으면 null */
export type Dyad = (mine: number, other: number) => number | null;

export type DyadSpec = {
  /** 이 링크의 축 — 두 크랭크가 이 축에 수직인 한 평면에서 돈다 */
  axis: Vec3;
  /** 내 크랭크의 축·피벗 */
  crankPivot: Vec3;
  /** 반대편 크랭크의 피벗 */
  otherPivot: Vec3;
  /** 정지 자세의 두 크랭크 핀과, 두 링크가 만나는 점 */
  pin: Vec3; otherPin: Vec3; joint2: Vec3;
};

/**
 * <b>두 크랭크가 함께 미는 5절 링크</b>(스파이로그래프·핀토그래프).
 *
 * <p>크랭크–로커는 미는 쪽이 하나였다. 여기서는 <b>둘</b>이다 — 두 크랭크가 서로 다른 속도로 돌고,
 * 두 막대가 만나는 점이 그 둘에 <b>동시에</b> 매여 있다. 그래서 한쪽만 보고는 정할 수 없다.
 *
 * <p>푸는 법은 오히려 단순하다. 만나는 점은 <b>두 원의 교점</b>이다 — 내 핀에서 내 막대 길이만큼,
 * 상대 핀에서 상대 막대 길이만큼. 닫힌 식이 있어 뉴턴법이 필요 없다.
 * <p>교점은 둘이라(위로 꺾인 팔꿈치·아래로 꺾인 팔꿈치) <b>정지 자세에서 있던 쪽</b>을 계속 고른다.
 *
 * <p>두 링크가 각자 이 풀이를 쓰지만 <b>같은 점</b>을 얻는다(원 둘의 교점은 어느 쪽에서 재도 같다).
 * 그래서 선회–피치 링크와 달리 풀이를 나눠 쓸 필요가 없다.
 */
export function makeDyad(s: DyadSpec): Dyad {
  const u = unit(s.axis);
  const pA = sub(s.pin, s.crankPivot), pB = sub(s.otherPin, s.otherPivot);
  const LA = len(sub(s.joint2, s.pin)), LB = len(sub(s.joint2, s.otherPin));
  const d0 = sub(s.joint2, s.pin);
  const flat = (d: Vec3): Vec3 => { const k = dot(d, u); return [d[0] - u[0] * k, d[1] - u[1] * k, d[2] - u[2] * k]; };

  let meet: Vec3 = s.joint2;                                 // 지난 교점 — 두 답 중 어느 쪽인지 고르는 기준
  let was = { mine: 0, other: 0 };

  /** 한 걸음 — 두 원의 교점을 닫힌 식으로 구한다. 닿지 않으면 false */
  const step = (mine: number, other: number): boolean => {
    const a = add(s.crankPivot, rot(u, mine, pA));
    const b = add(s.otherPivot, rot(u, other, pB));
    const d = flat(sub(b, a)), L = len(d);
    if (L < 1e-9) return false;                              // 두 핀이 겹쳤다 — 교점이 원이 된다
    const e = mul(d, 1 / L);
    const x = (LA * LA - LB * LB + L * L) / (2 * L);
    const h2 = LA * LA - x * x;
    if (h2 < 0) return false;                                // 두 막대를 이어도 핀 사이가 안 닿는다
    const h = Math.sqrt(h2), base = add(a, mul(e, x)), n = cross(u, e);
    const c1 = add(base, mul(n, h)), c2 = sub(base, mul(n, h));
    meet = len(sub(c1, meet)) <= len(sub(c2, meet)) ? c1 : c2;
    return true;
  };

  return (mine, other) => {
    const dm = mine - was.mine, doo = other - was.other;
    // 해가 둘이라 많이 뛰면 반대쪽으로 넘어간다 — 크랭크–로커와 같은 이유로 나눠 따라간다
    const n = Math.min(Math.max(Math.ceil(Math.max(Math.abs(dm), Math.abs(doo)) / STEP), 1), 4096);
    const keep = meet;
    for (let i = 1; i <= n; i++) {
      if (!step(was.mine + (dm * i) / n, was.other + (doo * i) / n)) {
        meet = keep;                                         // 닿지 않는 데다 — 지난 자세를 흐트러뜨리지 않는다
        return null;
      }
    }
    was = { mine, other };
    // 막대는 제 크랭크에 매달려 있다 — 월드 기울기에서 크랭크 회전을 뺀다
    const P = add(s.crankPivot, rot(u, mine, pA));
    return signedAngle(u, d0, sub(meet, P)) - mine;
  };
}
